/**
 * "Geen automatisch verval" (specs/01): when granting access, a credential or a group
 * membership the administrator must explicitly choose between an end date and no end
 * date. There is never a preselected default.
 */

export type ExpiryChoice =
  | { mode: 'unset' }
  | { mode: 'none' }
  | { mode: 'until'; date: string }; // date: 'YYYY-MM-DD' (local) or ISO string

export const UNSET_EXPIRY: ExpiryChoice = { mode: 'unset' };

export interface ExpiryContext {
  /** End date of the parent group, if any. A member's end date may not exceed it. */
  groupValidUntil?: string | null;
  /** When true (temp codes, installer assignments) "Geen einddatum" is not allowed. */
  endDateRequired?: boolean;
  now?: Date;
}

export type ExpiryResult =
  | { ok: true; validUntil: string | null }
  | { ok: false; error: string };

/** Converts a 'YYYY-MM-DD' to the end of that local day as ISO; ISO strings pass through. */
export function endOfDayIso(date: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split('-').map(Number) as [number, number, number];
    return new Date(y, m - 1, d, 23, 59, 59).toISOString();
  }
  return new Date(date).toISOString();
}

export function resolveExpiry(choice: ExpiryChoice, ctx: ExpiryContext = {}): ExpiryResult {
  const now = ctx.now ?? new Date();
  switch (choice.mode) {
    case 'unset':
      return {
        ok: false,
        error: ctx.endDateRequired
          ? 'Kies een einddatum.'
          : 'Kies "Einddatum kiezen" of "Geen einddatum".',
      };
    case 'none':
      if (ctx.endDateRequired) return { ok: false, error: 'Hier is een einddatum verplicht.' };
      return { ok: true, validUntil: null };
    case 'until': {
      if (!choice.date) return { ok: false, error: 'Vul een einddatum in.' };
      const iso = endOfDayIso(choice.date);
      const t = new Date(iso).getTime();
      if (Number.isNaN(t)) return { ok: false, error: 'Deze datum klopt niet.' };
      if (t <= now.getTime()) return { ok: false, error: 'De einddatum ligt in het verleden.' };
      if (ctx.groupValidUntil && t > new Date(ctx.groupValidUntil).getTime()) {
        return {
          ok: false,
          error: 'De einddatum mag niet later zijn dan de einddatum van de groep.',
        };
      }
      return { ok: true, validUntil: iso };
    }
  }
}
